import {
  MenuList as MuiMenuList,
  MenuItem as MuiMenuItem,
  ListItemText,
  ListItemIcon as MuiListItemIcon,
  Typography
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { FC, useState } from 'react';
import { Checkbox } from '../../../atomic';

type MenuItemProps = {
  label: string;
  isWarning?: boolean;
  onClick: () => void;
};

type MenuIconItemProps = MenuItemProps & {
  icon: JSX.Element;
};

type MenuCheckboxItemProps = Omit<MenuItemProps, 'onClick'> & {
  checked: boolean;
};

type Props = {
  config: Array<MenuItemProps> | Array<MenuIconItemProps> | Array<MenuCheckboxItemProps>;
  closeMenu: () => void;
};

export type MenuListProps = Pick<Props, 'config'>;

const MenuItem = styled(MuiMenuItem)(({ theme }) => ({
  padding: '0 24px',
  height: '40px',
  margin: '0 0 8px',
  ':last-child': {
    margin: '0'
  },
  ':hover': {
    backgroundColor: theme.palette.grey[50]
  }
}));

const ListItemIcon = styled(MuiListItemIcon)({
  ['> svg']: {
    width: '24px',
    height: '24px',
    paddingRight: '16px'
  }
});

const ListCheckbox = styled(Checkbox)({
  paddingRight: '16px'
});

export const MenuList: FC<Props> = ({ config, closeMenu }) => {
  const [items, setItems] = useState(config);

  return (
    <MuiMenuList
      sx={(theme) => ({
        paddingY: '24px',
        '.MuiTouchRipple-child': {
          backgroundColor: theme.palette.grey[50]
        }
      })}
    >
      {items.map((item) => (
        <MenuItem
          key={item.label}
          onClick={() => {
            if ('checked' in item) {
              item.checked = !item.checked;
              setItems([...items] as Array<MenuCheckboxItemProps>);
            } else {
              item.onClick();
              closeMenu();
            }
          }}
        >
          {'icon' in item && (
            <ListItemIcon
              sx={(theme) => ({
                color: item.isWarning ? theme.palette.error.main : theme.palette.primary.main
              })}
            >
              {item.icon}
            </ListItemIcon>
          )}
          {'checked' in item && <ListCheckbox checked={item.checked} />}
          <ListItemText>
            <Typography
              sx={(theme) => ({
                fontSize: '1.0rem',
                color: item.isWarning ? theme.palette.error.main : theme.palette.primary.main
              })}
            >
              {item.label}
            </Typography>
          </ListItemText>
        </MenuItem>
      ))}
    </MuiMenuList>
  );
};
